import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';

const MAX_FILE_SIZE = parseInt(process.env.REACT_APP_MAX_FILE_SIZE || '5368709120', 10);
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

const ACCEPT = {
  'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'],
  'audio/*': ['.mp3', '.wav', '.flac', '.aac', '.ogg', '.wma', '.m4a'],
};

const VALID_TYPES = [
  'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/quicktime',
  'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/ogg', 'audio/wma', 'audio/m4a',
];

const rejectionMessage = (code) => {
  if (code === 'file-too-large') return `File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`;
  if (code === 'file-invalid-type') return 'Only video and audio files are allowed.';
  return 'This file cannot be uploaded.';
};

export default function FileDropzone({ onFileSelected }) {
  const [error, setError] = useState(null);

  const onDrop = useCallback((accepted, rejected) => {
    setError(null);
    if (rejected?.length) {
      setError(rejectionMessage(rejected[0].errors[0]?.code));
      return;
    }
    const file = accepted?.[0];
    if (!file) return;
    if (!VALID_TYPES.includes(file.type)) {
      setError(rejectionMessage('file-invalid-type'));
      return;
    }
    onFileSelected(file);
  }, [onFileSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  return (
    <>
      <div {...getRootProps()} className={`upload-dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        <div className="upload-dropzone-icon"><CloudArrowUpIcon /></div>
        <div className="upload-dropzone-text">
          <h3>{isDragActive ? 'Drop the file here' : 'Drag a file here or click to browse'}</h3>
        </div>
        <div className="upload-dropzone-meta">
          MP4, MOV, WEBM, MP3, WAV, M4A, FLAC, OGG
          <span className="upload-dropzone-dot">·</span>
          up to {MAX_FILE_SIZE_MB} MB
        </div>
      </div>
      {error && <p className="upload-error" role="alert">{error}</p>}
    </>
  );
}
